import React from 'react';
import { cn } from '@/lib/utils';
import logo from '@/assets/brand/protega-logo.png';
import logoOnDark from '@/assets/brand/protega-logo-on-dark.png';
import { useTheme } from './theme/ThemeProvider';

type LogoSize = 'md' | 'sm' | 'xs';

interface LogoProps {
  /** md: sign-in pages · sm: sidebar · xs: mobile app bar. */
  size?: LogoSize;
  className?: string;
}

// The brand artwork is 484 × 74; widths keep that ratio so the image never stretches.
const sizes: Record<LogoSize, { width: number; height: number }> = {
  md: { width: 144, height: 22 },
  sm: { width: 118, height: 18 },
  xs: { width: 105, height: 16 },
};

/** Protega wordmark: the official logo artwork, with white lettering in dark mode. */
const Logo: React.FC<LogoProps> = ({ size = 'md', className }) => {
  const { theme } = useTheme();
  const { width, height } = sizes[size];
  return (
    <img
      src={theme === 'dark' ? logoOnDark : logo}
      alt="Protega"
      width={width}
      height={height}
      draggable={false}
      className={cn('tw-block tw-shrink-0 tw-select-none', className)}
    />
  );
};

/** Compact "E" mark used where the full wordmark doesn't fit (tablet nav rail). */
export const LogoMark: React.FC<{ className?: string }> = ({ className }) => (
  <span
    role="img"
    aria-label="Protega"
    className={cn('tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-xl tw-bg-ds-text-primary', className)}
  >
    <span aria-hidden className="tw-flex tw-flex-col tw-gap-[3px]">
      <span className="tw-h-1 tw-w-[18px] tw-bg-ds-brand" />
      <span className="tw-h-1 tw-w-[18px] tw-bg-ds-surface" />
      <span className="tw-h-1 tw-w-[18px] tw-bg-ds-surface" />
    </span>
  </span>
);

export default Logo;
